/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { Selector, Selector$inboundSchema } from "./selector.js";

/**
 * active: authorizing requests. expired: its deadline passed. revoked: ended early by this request.
 */
export const Status = {
  Active: "active",
  Expired: "expired",
  Revoked: "revoked",
} as const;
/**
 * active: authorizing requests. expired: its deadline passed. revoked: ended early by this request.
 */
export type Status = OpenEnum<typeof Status>;

export type TemporaryGrant = {
  /**
   * When the grant was given, or restarted after expiring.
   */
  createdAt: Date;
  /**
   * When the grant stops, or stopped, authorizing new requests.
   */
  expiresAt: Date;
  /**
   * Temporary grant ID.
   */
  id: string;
  /**
   * For an active grant, the principal URNs of the holder's other grants that already give the same access: for a member or agent, a role, everyone (user:all), or the holder itself for a grant made to it directly; for a role or everyone, only that principal's own other grants. Access through these remains when this grant ends. Empty for ended grants and when this grant is the only route.
   */
  otherAccess: Array<string>;
  /**
   * Canonical principal URN holding the access.
   */
  principalUrn: string;
  /**
   * Seconds until the deadline while active; 0 once ended.
   */
  remainingSeconds: number;
  /**
   * The scope given.
   */
  scope: string;
  /**
   * A constraint that narrows which resources a grant applies to.
   */
  selector: Selector;
  /**
   * active: authorizing requests. expired: its deadline passed. revoked: ended early by this request.
   */
  status: Status;
  updatedAt: Date;
};

/** @internal */
export const Status$inboundSchema: z.ZodMiniType<Status, unknown> = openEnums
  .inboundSchema(Status);

/** @internal */
export const TemporaryGrant$inboundSchema: z.ZodMiniType<
  TemporaryGrant,
  unknown
> = z.pipe(
  z.object({
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    expires_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    id: z.string(),
    other_access: z.array(z.string()),
    principal_urn: z.string(),
    remaining_seconds: z.int(),
    scope: z.string(),
    selector: Selector$inboundSchema,
    status: Status$inboundSchema,
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "expires_at": "expiresAt",
      "other_access": "otherAccess",
      "principal_urn": "principalUrn",
      "remaining_seconds": "remainingSeconds",
      "updated_at": "updatedAt",
    });
  }),
);

export function temporaryGrantFromJSON(
  jsonString: string,
): SafeParseResult<TemporaryGrant, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => TemporaryGrant$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'TemporaryGrant' from JSON`,
  );
}
